import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ApiError, api, type RedeemApp, type RedeemAppDetail, type RedeemIssued } from '@/api/client'
import { useRequests, useSchedule } from '@/lib/queries'
import { useInvalidateRedeem } from '@/lib/redeem'
import { formatShort } from '@/lib/time'
import { Button, ConfirmDialog, CopyButton, EmptyState, Field, Input, PlatformMark, StatTile, StatusPill, When, errorText, useToast } from '@/ui'
import { ItemRow } from '../schedule/ScheduleList'
import { usePostCommands } from '../schedule/shared'
import { DeskSettings, Section } from './shared'

const SCHEDULED = { source: 'redeem', status: 'scheduled,publishing,failed' } as const

/** An app's front page: the pool in four numbers, a code to take, the desk's
 *  settings, the latest requests and the posts about to go up. */
export default function AppOverview({ detail }: { detail: RedeemAppDetail }) {
  const { app, stats } = detail
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="남음"
          value={stats.available}
          hint={`전체 ${stats.total}장`}
          tone={stats.available === 0 && stats.total > 0 ? 'failed' : undefined}
        />
        <StatTile
          label="나감"
          value={stats.issued}
          hint={stats.last_issued_at ? `마지막 ${formatShort(stats.last_issued_at)}` : '아직 없음'}
        />
        <StatTile label="오늘" value={stats.issued_today} hint={`24시간 상한 ${app.daily_cap || 20}장`} />
        <StatTile label="무효" value={stats.void} hint="회수했거나 쓸 수 없는 코드" tone={stats.void > 0 ? 'failed' : undefined} />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0 space-y-6">
          <TakeCode app={app} />
          <RecentRequests appId={app.id} />
        </div>
        <div className="min-w-0 space-y-6">
          <Section title="데스크 설정" hint="이 앱이 얼마나 스스로 나눠 주는지.">
            <DeskSettings app={app} />
          </Section>
          <AppScheduled appId={app.id} />
        </div>
      </div>
    </div>
  )
}

/** The server's reasons to ask twice before a manual handout (HTTP 409 with
 *  `warnings`): past the daily cap, or to someone who already has a code. */
type TakeWarning = { kind: 'cap' | 'repeat'; message: string }

function takeWarnings(e: unknown): TakeWarning[] {
  if (!(e instanceof ApiError) || e.status !== 409) return []
  const w = (e.body as { warnings?: TakeWarning[] } | null)?.warnings
  return Array.isArray(w) ? w : []
}

/** Marks codes as handed out and renders the message to paste. Sending is the
 *  operator's to do. It follows the rules of the automatic handout: an ended
 *  giveaway gives nothing, and going past the daily cap or giving a second
 *  code to the same person is asked about first. */
function TakeCode({ app }: { app: RedeemApp }) {
  const [recipient, setRecipient] = useState('')
  const [channel, setChannel] = useState('reddit')
  const [note, setNote] = useState('')
  const [count, setCount] = useState(1)
  // A web app's codes are shared: say which one (the post's DM code).
  const [code, setCode] = useState('')
  const web = app.platform === 'web'
  const [issued, setIssued] = useState<RedeemIssued[]>([])
  const invalidate = useInvalidateRedeem()
  const toast = useToast()
  const [asking, setAsking] = useState<TakeWarning[]>([])
  const ended = !!app.ended_at
  const take = useMutation({
    mutationFn: (override: TakeWarning[] = []) =>
      api.issueRedeemCodes(app.id, {
        recipient,
        count: web ? 1 : count,
        channel,
        note,
        ...(web ? { code } : {}),
        ...(override.some((w) => w.kind === 'cap') ? { override_cap: true } : {}),
        ...(override.some((w) => w.kind === 'repeat') ? { override_repeat: true } : {}),
      }),
    onSuccess: async (res) => {
      setAsking([])
      setIssued(res.items ?? [])
      toast.success(`${res.items?.length ?? 0}장을 꺼냈다. 나간 것으로 센다.`)
      await invalidate()
    },
    onError: (e) => {
      setAsking(takeWarnings(e))
    },
  })
  return (
    <Section title="코드 꺼내기" hint="코드를 나간 것으로 표시하고 보낼 메시지를 만든다. 보내는 일은 직접 한다.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="받는 사람" hint="u/someone, 이메일, 이름">
          <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} />
        </Field>
        {web ? (
          <Field label="공유 코드" hint="한 번 쓰는 코드. 예: WRITING20">
            <Input className="font-mono" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
          </Field>
        ) : (
          <Field label="장수">
            <Input type="number" min={1} max={25} value={count} onChange={(e) => setCount(Number(e.target.value))} />
          </Field>
        )}
        <Field label="채널">
          <Input value={channel} onChange={(e) => setChannel(e.target.value)} />
        </Field>
        <Field label="메모" hint="어디서 온 사람인지">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {ended && <p className="text-table text-status-failed">이 나눔은 끝났다. 다시 열기 전에는 코드를 꺼낼 수 없다.</p>}
      {take.error && asking.length === 0 && <p className="text-table text-status-failed">{errorText(take.error)}</p>}
      <Button variant="primary" loading={take.isPending && asking.length === 0} disabled={!recipient.trim() || ended} onClick={() => take.mutate([])}>
        코드 꺼내기
      </Button>
      <ConfirmDialog
        open={asking.length > 0}
        onOpenChange={(o) => !o && !take.isPending && setAsking([])}
        title="그래도 꺼낼까?"
        description={
          <span className="block space-y-2">
            {asking.map((w) => (
              <span key={w.kind} className="block">
                {w.message}
              </span>
            ))}
            <span className="block">꺼낸 코드는 나간 것으로 세고, 받은 사람과 채널이 기록에 남는다.</span>
          </span>
        }
        confirmLabel="그래도 꺼낸다"
        confirmVariant="destructive"
        loading={take.isPending}
        onConfirm={async () => {
          await take.mutateAsync(asking).catch(() => {})
        }}
      />
      {issued.map((it) => (
        <div key={it.code.id} className="space-y-2 rounded-md border border-border bg-background p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <code className="break-all font-mono text-body">{it.code.code}</code>
            <span className="flex flex-wrap gap-1.5">
              <CopyButton text={it.code.code} what="코드" label="코드 복사" />
              <CopyButton text={it.code.redeem_url} what="링크" label="링크 복사" />
              <CopyButton text={it.message} what="메시지" label="메시지 복사" />
            </span>
          </div>
          <a href={it.code.redeem_url} target="_blank" rel="noreferrer" className="block break-all text-meta text-muted-foreground hover:underline">
            {it.code.redeem_url}
          </a>
          <div className="text-meta text-muted-foreground">DM 제목: {it.dm_subject}</div>
          <pre className="whitespace-pre-wrap break-words font-sans text-table">{it.message}</pre>
        </div>
      ))}
    </Section>
  )
}

function RecentRequests({ appId }: { appId: number }) {
  const q = useRequests('', appId, 5)
  const items = q.data?.items ?? []
  return (
    <Section
      title="최근 요청"
      actions={
        <Link to={`/redeem/${appId}/requests`} className="text-meta text-accent hover:underline">
          전체 보기 →
        </Link>
      }
    >
      {items.length === 0 ? (
        <p className="text-table text-muted-foreground">{q.isPending ? '불러오는 중' : '요청이 없다'}</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((r) => (
            <li key={r.id} className="flex items-center gap-2 py-2 text-table">
              <PlatformMark platform={r.platform} />
              <span className="min-w-0 flex-1 truncate font-medium">{r.platform === 'bluesky' ? '@' : 'u/'}{r.requester}</span>
              <When iso={r.requested_at} className="text-meta text-muted-foreground" />
              <StatusPill domain="request" status={r.state} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/** The posts of this app about to go up, or failing to. */
function AppScheduled({ appId }: { appId: number }) {
  const q = useSchedule({ ...SCHEDULED, appId })
  const commands = usePostCommands()
  const items = q.data?.items ?? []
  const shown = items.slice(0, 3)
  const now = new Date()
  return (
    <Section
      title="예약된 글"
      actions={
        <Link to={`/schedule?source=redeem&app_id=${appId}`} className="text-meta text-accent hover:underline">
          일정에서 보기 →
        </Link>
      }
    >
      {shown.length === 0 ? (
        q.isPending ? (
          <p className="text-table text-muted-foreground">불러오는 중</p>
        ) : (
          <EmptyState title="예약된 글이 없다" description="글 탭에서 쓰거나 일정에서 시각을 정한다." className="py-6" />
        )
      ) : (
        <ul className="-mx-4 divide-y divide-border border-y border-border">
          {shown.map((it) => (
            <li key={`${it.kind}-${it.id}`}>
              <ItemRow it={it} commands={commands} now={now} />
            </li>
          ))}
        </ul>
      )}
      {items.length > shown.length && <p className="text-meta text-muted-foreground">외 {items.length - shown.length}건</p>}
      {commands.dialog}
    </Section>
  )
}
